import { z } from "zod/v4";

export const leadStatusSchema = z.enum([
  "new",
  "engaged",
  "won",
  "unqualified",
  "lost",
]);
export type LeadStatus = z.infer<typeof leadStatusSchema>;

export type LeadChannel = "sms" | "yelp";

export type MessageEvent = {
  id: string;
  type: "message";
  author_type: "agent" | "user";
  author_name: string;
  body: string;
  timestamp: string;
};

export type CallEvent = {
  id: string;
  type: "call";
  author_type: "agent" | "user";
  author_name: string;
  direction: "inbound" | "outbound";
  duration_seconds: number;
  timestamp: string;
  transcript: string | null;
};

export type TimelineEvent = MessageEvent | CallEvent;

export type Lead = {
  id: string;
  contact_name: string;
  phone: string;
  email: string;
  status: LeadStatus;
  services: string[];
  city: string;
  state: string;
  channel: LeadChannel;
  last_activity_at: string;
  created_at: string;
  address: string;
  availability: string;
  project_description: string;
  source: string;
  source_url: string | null;
  service_titan_customer_url: string | null;
  service_titan_booking_url: string | null;
  service_titan_job_url: string | null;
  message_count: number;
  call_count: number;
  ai_summary: string;
  timeline: TimelineEvent[];
};

export type LeadListItem = {
  id: string;
  status: LeadStatus;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  project_services: string[] | null;
  project_postal_code: string | null;
  project_address_1: string | null;
  project_city: string | null;
  project_state: string | null;
  campaign_id: string | null;
  integration_name: string | null;
  message_count: number;
  call_count: number;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string | null;
};

export const leadProviderSchema = z.enum([
  "yelp",
  "angi",
  "directorii",
  "elocal",
  "homebuddy",
  "roofr",
  "modernize",
  "networx",
  "nextdoor",
  "owenscorning",
  "thumbtack",
]);
export type LeadProvider = z.infer<typeof leadProviderSchema>;

export const questionnaireEntrySchema = z.object({
  question: z.string().nullable(),
  answers: z.array(z.string().nullable()).nullable(),
});
export type QuestionnaireEntry = z.infer<typeof questionnaireEntrySchema>;

// Matches `GetLeadResponse` from platform.api (src/schema/api/leads/get.ts).
export const leadDetailSchema = z.object({
  id: z.string(),
  source: leadProviderSchema,
  source_lead_id: z.string(),
  source_business_id: z.string(),
  source_url: z.string().nullish(),
  campaign_id: z.string().nullish(),
  status: leadStatusSchema,
  contact_name: z.string(),
  contact_email: z.string().nullish(),
  contact_phone: z.string().nullish(),
  project_services: z.array(z.string()).nullish(),
  project_address_1: z.string().nullish(),
  project_address_2: z.string().nullish(),
  project_city: z.string().nullish(),
  project_state: z.string().nullish(),
  project_postal_code: z.string().nullish(),
  project_country: z.string().nullish(),
  project_details: z.string().nullish(),
  project_availability: z.string().nullish(),
  project_availability_dates: z.array(z.string()).nullish(),
  project_questionnaire: z.array(questionnaireEntrySchema).nullish(),
  servicetitan_customer_id: z.string().nullish(),
  servicetitan_job_id: z.string().nullish(),
  servicetitan_booking_id: z.string().nullish(),
  ai_summary: z.string().nullish(),
  created_at: z.string(),
  updated_at: z.string().nullish(),
});
export type LeadDetail = z.infer<typeof leadDetailSchema>;

export type CampaignDetailStats = {
  total_leads: number;
  engaged_count: number;
  engaged_percent: number;
  won_count: number;
  won_percent: number;
  conversion_rate: number;
  lost_count: number;
  unqualified_count: number;
};
